import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import { Spinner } from "components/atoms";
import { ConsultationRecord } from "common/models/types";
import { fetchConsultationRecordById } from "api/consultationService";
import { log, LogLevel } from "utils/LogUtil";

/**
 * rehypeRaw 경유 원시 HTML의 실행·외부 로드 방어(spec 설계 결정 12).
 * 텍스트 기록의 자유 글이 record_content에 들어가므로 저장형 XSS 벡터를 전역 제거한다.
 * MarkdownViewer와 같은 목록.
 */
const DISALLOWED_ELEMENTS = ["script", "iframe", "style", "form", "object", "embed", "link", "base"];

interface ConsultationRecordDetailProps {
  recordId: number;
}

// --- Styled Components ---
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")[700]};
  padding-bottom: 8px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};
`;

const AudioSection = styled.div`
  audio {
    width: 100%;
  }
`;

const MarkdownSection = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[50]};

  h1, h2, h3, h4, h5, h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #1f2937;
  }

  h1 { font-size: 24px; }
  h2 { font-size: 20px; }
  h3 { font-size: 18px; }

  p {
    margin-bottom: 12px;
    line-height: 1.6;
  }

  ul, ol {
    margin-left: 20px;
    margin-bottom: 12px;
  }

  li {
    margin-bottom: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
  }

  th, td {
    border: 1px solid #e5e7eb;
    padding: 8px 12px;
    text-align: left;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
  }

  pre {
    background: #1f2937;
    color: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin-bottom: 16px;
  }

  blockquote {
    border-left: 4px solid #4a90e2;
    padding-left: 16px;
    margin: 16px 0;
    color: #6b7280;
  }
`;

const formatDate = (date: Date | string): string => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
};

const ConsultationRecordDetail: React.FC<ConsultationRecordDetailProps> = ({
  recordId,
}) => {
  const [record, setRecord] = useState<ConsultationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRecord = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchConsultationRecordById(recordId);
        if (response.error || !response.data) {
          setError(response.message || "상담 기록을 불러오는데 실패했습니다.");
          return;
        }
        setRecord(response.data);
      } catch (err) {
        log(LogLevel.ERROR, "상담 기록 상세 조회 실패:", err);
        setError("상담 기록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [recordId]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner size="Large" />
      </LoadingContainer>
    );
  }

  if (error || !record) {
    return (
      <Container>
        <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")[500]}>
          {error || "상담 기록을 찾을 수 없습니다."}
        </Text>
      </Container>
    );
  }

  return (
    <Container>
      <Text textType="H3" color={COLOR_SYSTEM.get("Gray")[700]} element="h3">
        상담 기록
      </Text>
      {record.createdAt && (
        <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[400]}>
          {formatDate(record.createdAt)}
        </Text>
      )}

      {record.audioUrl && (
        <Section>
          <SectionTitle>오디오</SectionTitle>
          <AudioSection>
            <audio controls src={record.audioUrl}>
              브라우저가 오디오를 지원하지 않습니다.
            </audio>
          </AudioSection>
        </Section>
      )}

      {record.recordContent && (
        <Section>
          <SectionTitle>기록지 내용</SectionTitle>
          <MarkdownSection>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              disallowedElements={DISALLOWED_ELEMENTS}
            >
              {record.recordContent}
            </ReactMarkdown>
          </MarkdownSection>
        </Section>
      )}
    </Container>
  );
};

export default ConsultationRecordDetail;
